import { useMemo } from "react";
import type { PhotoReportCoverRowBody } from "@rebuild/shared/models";
import { EditableText } from "../../EditableText";
import type { PhotoGalleryGroup } from "../../details-section/Photos/photos.types";
import { PhotoReportRoomSection } from "./PhotoReportRoomSection";
import { getPhotoRef, getRoomTitle, isPhotoIncluded, type PhotoSelectionState, type RoomTitleState } from "./photoReport.utils";

interface PhotoReportPreviewProps {
  organizationName: string;
  letterheadUrl: string | null;
  letterheadAlignment?: "left" | "center" | "right";
  includeCoverPage: boolean;
  includeTableOfContents: boolean;
  coverRows: PhotoReportCoverRowBody[];
  onCoverRowChange: (index: number, value: string) => void;
  groups: PhotoGalleryGroup[];
  roomTitles: RoomTitleState;
  onRoomTitleChange: (groupId: string, title: string) => void;
  selection: PhotoSelectionState;
  photoNumberById: Map<string, number>;
  showPhotoNumberOverlay: boolean;
  includeCaptions: boolean;
  onTogglePhoto: (galleryId: string) => void;
  onCaptionChange: (galleryId: string, caption: string) => void;
  onToggleRoom: (galleryIds: string[], include: boolean) => void;
}

const ALIGNMENT_CLASS: Record<"left" | "center" | "right", string> = {
  left: "items-start",
  center: "items-center",
  right: "items-end",
};

export const PhotoReportPreview = ({
  organizationName,
  letterheadUrl,
  letterheadAlignment = "left",
  includeCoverPage,
  includeTableOfContents,
  coverRows,
  onCoverRowChange,
  groups,
  roomTitles,
  onRoomTitleChange,
  selection,
  photoNumberById,
  showPhotoNumberOverlay,
  includeCaptions,
  onTogglePhoto,
  onCaptionChange,
  onToggleRoom,
}: PhotoReportPreviewProps) => {
  // Rooms that will actually appear in the report — i.e. have at least one included, exportable
  // photo — mirroring what the export request and PDF TOC contain. Memoized so rapid interactions
  // (e.g. typing captions) don't rescan every group/photo on each keystroke.
  const tocRoomTitles = useMemo(
    () =>
      groups
        .filter((group) =>
          group.photos.some((photo) => getPhotoRef(photo) !== null && isPhotoIncluded(selection, photo.galleryId)),
        )
        .map((group) => getRoomTitle(group, roomTitles)),
    [groups, selection, roomTitles],
  );

  return (
    <div className="flex-[2] overflow-y-auto rounded-lg bg-gray-100 p-6">
      <div className="mx-auto max-w-2xl rounded-lg bg-white p-8 shadow-sm">
        {/* Branding header — mirrors the PDF letterhead. Shows the org logo when configured, falling
            back to the text-only org name. Honors the letterhead's preview alignment. */}
        {(letterheadUrl || organizationName) && (
          <div className={`mb-6 flex flex-col gap-1.5 ${ALIGNMENT_CLASS[letterheadAlignment]}`}>
            {letterheadUrl && (
              <img
                src={letterheadUrl}
                alt={organizationName ? `${organizationName} logo` : "Organization logo"}
                className="max-h-20 w-auto object-contain"
                loading="lazy"
                decoding="async"
              />
            )}
            {organizationName && (
              <div className="text-sm font-semibold tracking-wide text-gray-500 uppercase">{organizationName}</div>
            )}
          </div>
        )}

        {includeCoverPage && (
          <div className="mb-8">
            <h2 className="mb-6 text-center text-2xl font-bold text-gray-900">Photo Report</h2>
            <dl className="space-y-1.5">
              {coverRows.map((row, index) => (
                <div key={row.label} className="flex gap-2 text-sm">
                  <dt className="w-32 shrink-0 font-medium text-gray-500">{row.label}</dt>
                  <dd className="text-gray-900">
                    <EditableText
                      value={row.value ?? ""}
                      onChange={(value) => onCoverRowChange(index, value)}
                      placeholder="—"
                    />
                  </dd>
                </div>
              ))}
            </dl>
          </div>
        )}

        {includeTableOfContents && tocRoomTitles.length > 0 && (
          <div className="mb-8">
            <h3 className="mb-3 text-center text-lg font-bold text-gray-900">Table of Contents</h3>
            <ul className="space-y-1.5">
              {tocRoomTitles.map((title, index) => (
                <li key={`${title}-${index}`} className="flex items-baseline gap-2 text-sm text-gray-700">
                  <span className="shrink-0">{title}</span>
                  <span className="min-w-0 flex-1 translate-y-[-3px] border-b border-dotted border-gray-300" />
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="flex flex-col gap-6">
          {groups.map((group) => (
            <PhotoReportRoomSection
              key={group.id}
              group={group}
              titleValue={roomTitles.get(group.id) ?? (group.title || "Photos")}
              onTitleChange={(title) => onRoomTitleChange(group.id, title)}
              selection={selection}
              photoNumberById={photoNumberById}
              showPhotoNumberOverlay={showPhotoNumberOverlay}
              includeCaptions={includeCaptions}
              onTogglePhoto={onTogglePhoto}
              onCaptionChange={onCaptionChange}
              onToggleRoom={onToggleRoom}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
